/* eslint-disable @typescript-eslint/no-explicit-any */
import { MapRefs } from '@uiw/react-baidu-map';
import {
  LABEL_FEE_CARD_POSITION,
  LABEL_CARD_POSITION,
  FEES_CARD_POSITION,
  DEFAULT_CARD_POSITION,
  PIN_HEIGHT,
  PIN_FLEXI_HEIGHT,
  hardCodedAllLocale,
} from '../../constants/lib/constants';
import {
  checkThresholdPercentage,
  getCurrentRateStrikeThroughBlock,
  getCurrentPriceRateBlock,
  getPriceByDecimalPoint,
  getCurrRateAmountBlock,
  getCurrentRatePointsBlock,
  getPriceCommaSeparator,
  getRateBlock,
} from '../../organisms/SearchResults/lib/helper';
import {
  DefaultValue,
  RateNodeTypeToBeUsed,
  PointsNodeTypeToBeUsed,
  STATUS_NOT_AVAILABLE,
  STATUS_MAX_OCCUPANCY_EXCEEDED,
} from '../../organisms/SearchResults/index.constant';
import { handleBMapRecenter } from '@marriott/mi-ui-library';
import { fetchCurrencyToLocale } from '../../utils';
import { INFO_WINDOW_CONSTANTS } from './SmartInfoWindow/SmartInfoWindow.types';

// Re-export INFO_WINDOW_CONSTANTS as MAP_INFO_WINDOW_CONSTANTS for backward compatibility with tests
export const MAP_INFO_WINDOW_CONSTANTS = INFO_WINDOW_CONSTANTS;

/**
 * Function to generate label and class for secondary marker
 */
export const generateLabel = (
  dataValue: any,
  isMarker: boolean,
  selectedHotel: any,
  isFlexibleDateSearch: boolean,
  jsonContent: any,
  currentLocale?: string,
  noRatesAreAvailable?: boolean
) => {
  if (dataValue?.basicInformation?.newProperty) {
    return {
      label: `${
        !isMarker &&
        selectedHotel &&
        ((isFlexibleDateSearch ? selectedHotel[0]?.node?.id : selectedHotel[0]?.node.property.id) ||
          (isFlexibleDateSearch ? selectedHotel[1]?.node?.id : selectedHotel[1]?.node.property.id)) === dataValue.id
          ? `${jsonContent.newHotelLabel}`
          : !isMarker
          ? `${jsonContent.newHotelLabel}`
          : `${jsonContent.newTagLabel}`
      }`,
      className: `marker-label-add-on new-hotel new-hotel-${currentLocale} ${
        (isFlexibleDateSearch || noRatesAreAvailable) && 'flexible-new-hotel'
      }`,
    };
  } else if (dataValue?.basicInformation?.isRecentlyRenovated) {
    return {
      label: `${jsonContent.recentlyRenovatedLabel}`,
      className: 'marker-label-add-on recently-renovated',
    };
  } else if (dataValue?.basicInformation?.isFullyRenovated) {
    return {
      label: `${jsonContent.fullyRenovatedLabel}`,
      className: 'marker-label-add-on fully-renovated',
    };
  } else if (dataValue?.basicInformation?.hasRenovatedRooms) {
    return {
      label: `${jsonContent.renovatedRoomsLabel}`,
      className: 'marker-label-add-on renovated-room',
    };
  } else if (dataValue?.basicInformation?.newLobby) {
    return {
      label: `${jsonContent.renovatedLobbyLabel}`,
      className: 'marker-label-add-on renovated-lobby',
    };
  } else {
    return {
      label: '',
      className: '',
    };
  }
};

/**
 * Function to generate text of primary marker
 */
export const getLabelText = (
  edge: any,
  isFlexibleDateSearch: boolean,
  searchQueryState: any,
  isSpecialRateSearch: boolean,
  options: any,
  jsonContent: any,
  flexibleOrManyProperty: boolean,
  isErrorState: boolean,
  childCount: number,
  currentLocale: string,
  isShowFullPrice?: boolean,
  noRatesAreAvailable?: boolean
) => {
  let showStrikeThroughResult = false;
  let currEliteDiscountRate;
  let currRate = null;
  let currPointsRate;
  const currRatePointsBlock = getRateBlock(edge?.node?.rates, 'points');
  const rateNode = getCurrentPriceRateBlock(searchQueryState, edge.node.rates);

  // get rate points block for each property
  if (!isFlexibleDateSearch) {
    currPointsRate = getCurrentRatePointsBlock(edge?.node?.rates);
  }
  const pointsValue = flexibleOrManyProperty ? null : currPointsRate?.points ?? null;
  if (!isFlexibleDateSearch) {
    currRate = getCurrRateAmountBlock(rateNode);
  }
  // get total price for each property
  const totalPrice = flexibleOrManyProperty ? null : isShowFullPrice ? currRate?.totalAmount : null;
  // get rates for each property
  let priceInfo: any;
  // update logic for priceInfo depending on amountPlusMandatoryFees/amount
  if (currRate?.amountPlusMandatoryFees?.amount && hardCodedAllLocale.en === currentLocale) {
    priceInfo = flexibleOrManyProperty ? null : currRate?.amountPlusMandatoryFees;
  } else {
    priceInfo = flexibleOrManyProperty ? null : currRate?.amount;
  }

  if (options?.rateRequestTypes?.length === 3) {
    currRate = isFlexibleDateSearch
      ? null
      : edge.node.rates[0]?.rateAmounts?.filter((key: any) => key.rateMode.code === PointsNodeTypeToBeUsed)[0];
  }
  if (options?.rateRequestTypes?.length === 2) {
    currEliteDiscountRate = isFlexibleDateSearch ? null : getCurrentRateStrikeThroughBlock(edge?.node?.rates);
  }

  const currRateNode = getCurrentPriceRateBlock(searchQueryState, edge?.node?.rates); // passing the complete API rate block to getCurrentPriceRateBlock to get the correct object(check API response it will make sense)
  const ratesData = getCurrRateAmountBlock(currRateNode); // extracting amount object from correct rate block
  const propertyData = isFlexibleDateSearch ? edge?.node : edge?.node?.property;
  const showUnavailableMessage = flexibleOrManyProperty
    ? false
    : (!Object.keys(ratesData?.amount || {}).length && propertyData?.basicInformation?.openingDate && !pointsValue) ||
      (rateNode?.status?.code === STATUS_NOT_AVAILABLE && currRatePointsBlock?.status?.code === STATUS_NOT_AVAILABLE);
  let hotelUnavailable = false;
  if (showUnavailableMessage) {
    hotelUnavailable = true;
  }
  const adultOnly = propertyData?.basicInformation?.isAdultsOnly;
  const isAdultOnlyEdgeCaseTriggered = adultOnly && childCount > 0;
  // check if the rates are present
  const isRatesPresent: boolean | undefined =
    Object.keys(currRate ?? {}).length > 0 || Object.keys(currPointsRate ?? {}).length > 0;
  if (
    (isFlexibleDateSearch ||
      (!isFlexibleDateSearch && Object.keys(ratesData?.amount || {}).length === 0 && !pointsValue)) &&
    !propertyData?.basicInformation?.newProperty &&
    new Date(
      isFlexibleDateSearch
        ? !edge?.node?.basicInformation?.openingDate
        : edge?.node?.property?.basicInformation?.openingDate
    ) < new Date()
  ) {
    hotelUnavailable = true;
  }

  let isHotelMaxOccupancyExceeded = false;

  if (
    !isFlexibleDateSearch &&
    (currRateNode?.status?.code === STATUS_MAX_OCCUPANCY_EXCEEDED ||
      currRatePointsBlock?.status?.code === STATUS_MAX_OCCUPANCY_EXCEEDED)
  ) {
    isHotelMaxOccupancyExceeded = true;
  }

  let isErrorStateValue: boolean = isErrorState;
  if (
    isErrorState &&
    (pointsValue ||
      totalPrice?.amount ||
      priceInfo?.amount ||
      isFlexibleDateSearch ||
      flexibleOrManyProperty ||
      isHotelMaxOccupancyExceeded ||
      hotelUnavailable)
  ) {
    isErrorStateValue = false;
  }

  let currVal: any;
  // update logic for currVal depending on amountPlusMandatoryFees/amount
  if (currRate?.amountPlusMandatoryFees?.amount && hardCodedAllLocale.en === currentLocale) {
    currVal = flexibleOrManyProperty ? null : currRate?.amountPlusMandatoryFees;
  } else {
    currVal = flexibleOrManyProperty ? null : currRate?.amount;
  }
  let markerText = `${currVal?.amount} ${currVal?.currency}`;
  if (
    new Date(
      isFlexibleDateSearch
        ? !edge?.node?.basicInformation?.openingDate
        : edge?.node?.property?.basicInformation?.openingDate
    ) > new Date() &&
    (isFlexibleDateSearch || !isRatesPresent)
  ) {
    markerText = jsonContent.openingSoonLabel;
  } else if (!propertyData?.basicInformation?.bookable) {
    markerText = jsonContent?.rateUnavailableLabel;
  } else if (adultOnly && childCount > 0) {
    markerText = jsonContent?.unavailableLabel;
  } else if (
    (hotelUnavailable && !isFlexibleDateSearch && !noRatesAreAvailable) ||
    (isErrorStateValue && !isAdultOnlyEdgeCaseTriggered)
  ) {
    if (!isSpecialRateSearch) {
      // markerText = jsonContent?.soldOutLabel;
      markerText = jsonContent?.unavailableLabel;
    } else {
      markerText = jsonContent?.rateUnavailableLabel;
    }
  } else if (pointsValue) {
    markerText = `${getPriceCommaSeparator(pointsValue, searchQueryState, currentLocale)} ${jsonContent.pointsLabel}`;
  }
  // add logic for text to show on marker pin in case of points unavailability
  else if (!pointsValue && searchQueryState?.pointsEnable) {
    const cashPerNightRate =
      ratesData?.amountPlusMandatoryFees?.amount && hardCodedAllLocale.en === currentLocale
        ? ratesData?.amountPlusMandatoryFees
        : ratesData?.amount;
    const cashTotalRate = isShowFullPrice ? ratesData?.totalAmount : null;
    const cashRateToDisplay = cashTotalRate?.amount ? cashTotalRate : cashPerNightRate;

    if (cashRateToDisplay?.amount) {
      markerText = `${getPriceByDecimalPoint(
        cashRateToDisplay.amount,
        cashRateToDisplay.decimalPoint,
        searchQueryState,
        currentLocale,
        cashRateToDisplay.currency
      )} ${
        searchQueryState?.currentCurrency !== DefaultValue
          ? fetchCurrencyToLocale(currentLocale, jsonContent?.currencyLabel, searchQueryState?.currentCurrency)
          : fetchCurrencyToLocale(currentLocale, jsonContent?.currencyLabel, cashRateToDisplay.currency)
      }`;
    }
  } else if (ratesData?.totalAmount?.amount && isShowFullPrice) {
    let totalPriceInfo: any = ratesData?.totalAmount;
    let EEOPriceInfo = currEliteDiscountRate?.totalAmount;

    if (searchQueryState.currentCurrency !== DefaultValue) {
      totalPriceInfo = flexibleOrManyProperty ? null : ratesData?.totalAmount;
      EEOPriceInfo = flexibleOrManyProperty ? null : currEliteDiscountRate?.totalAmount;
    }
    if (
      EEOPriceInfo &&
      EEOPriceInfo?.amount &&
      totalPriceInfo &&
      totalPriceInfo?.amount &&
      EEOPriceInfo?.amount < totalPriceInfo?.amount
    ) {
      showStrikeThroughResult = checkThresholdPercentage(totalPriceInfo?.amount, EEOPriceInfo?.amount);
    }
    markerText = `${
      showStrikeThroughResult
        ? getPriceByDecimalPoint(
            EEOPriceInfo?.amount,
            EEOPriceInfo?.decimalPoint as number,
            searchQueryState,
            currentLocale,
            EEOPriceInfo?.currency
          )
        : getPriceByDecimalPoint(
            totalPriceInfo?.amount,
            totalPriceInfo?.decimalPoint ?? 0,
            searchQueryState,
            currentLocale,
            totalPriceInfo?.currency
          )
    } ${
      searchQueryState.currentCurrency === DefaultValue
        ? fetchCurrencyToLocale(currentLocale, jsonContent?.currencyLabel, totalPriceInfo?.currency)
        : fetchCurrencyToLocale(currentLocale, jsonContent?.currencyLabel, searchQueryState.currentCurrency)
    }`;
  } else if (flexibleOrManyProperty || noRatesAreAvailable) {
    markerText = '.';
  } else {
    let priceInfoBlock: any;
    if (ratesData?.amountPlusMandatoryFees?.amount && hardCodedAllLocale.en === currentLocale) {
      priceInfoBlock = ratesData?.amountPlusMandatoryFees;
    } else {
      priceInfoBlock = ratesData?.amount ?? priceInfo;
    }
    let EEOPriceInfo;
    if (currEliteDiscountRate?.amountPlusMandatoryFees?.amount && hardCodedAllLocale.en === currentLocale) {
      EEOPriceInfo = currEliteDiscountRate?.amountPlusMandatoryFees;
    } else {
      EEOPriceInfo = currEliteDiscountRate?.amount;
    }
    if (searchQueryState.currentCurrency !== DefaultValue) {
      if (ratesData?.amountPlusMandatoryFees?.amount && hardCodedAllLocale.en === currentLocale) {
        priceInfoBlock = flexibleOrManyProperty ? null : ratesData?.amountPlusMandatoryFees;
      } else {
        priceInfoBlock = flexibleOrManyProperty ? null : ratesData?.amount;
      }
      EEOPriceInfo = flexibleOrManyProperty ? null : currEliteDiscountRate?.amount;
    }
    if (
      EEOPriceInfo &&
      EEOPriceInfo?.amount &&
      priceInfoBlock &&
      priceInfoBlock?.amount &&
      EEOPriceInfo?.amount < priceInfoBlock?.amount
    ) {
      showStrikeThroughResult = checkThresholdPercentage(priceInfoBlock?.amount, EEOPriceInfo?.amount);
    }
    markerText = `${
      showStrikeThroughResult
        ? getPriceByDecimalPoint(
            EEOPriceInfo?.amount,
            EEOPriceInfo?.decimalPoint as number,
            searchQueryState,
            currentLocale,
            EEOPriceInfo?.currency
          )
        : getPriceByDecimalPoint(
            priceInfoBlock?.amount,
            priceInfoBlock?.decimalPoint as number,
            searchQueryState,
            currentLocale,
            priceInfoBlock?.currency
          )
    } ${
      searchQueryState.currentCurrency === DefaultValue
        ? fetchCurrencyToLocale(currentLocale, jsonContent?.currencyLabel, priceInfoBlock?.currency)
        : fetchCurrencyToLocale(currentLocale, jsonContent?.currencyLabel, searchQueryState.currentCurrency)
    }`;
  }
  return markerText;
};

const isPlaceholderMarkerLabel = (labelText: string, isFlexibleDateSearch: boolean): boolean =>
  isFlexibleDateSearch || labelText === '.';

/**
 * Builds hover tooltip text without exposing the placeholder "." marker label.
 */
export const getMarkerTitleText = (
  generatedLabel: { label?: string } | undefined,
  labelText: string,
  isFlexibleDateSearch: boolean
): string => {
  const displayLabelText = isPlaceholderMarkerLabel(labelText, isFlexibleDateSearch) ? '' : labelText;

  if (generatedLabel?.label) {
    return displayLabelText ? `${generatedLabel.label} ${displayLabelText}`.trim() : generatedLabel.label;
  }

  return displayLabelText;
};

/**
 * Function to recenter map
 */
export const handleMapRecenter = (markerLatLng: any, mapRef: any) => {
  mapRef?.panTo(markerLatLng);
};

/**
 * Animation durations for smooth map transitions (in milliseconds)
 */
const ZOOM_ANIMATION_DURATION = 300;
const PAN_ANIMATION_DURATION = 300;

let mapAnimationFrameId: number | null = null;

/**
 * Easing function for smooth animation (ease-out cubic)
 */
const easeOutCubic = (t: number): number => {
  return 1 - Math.pow(1 - t, 3);
};

/**
 * Cancel any running map animation
 */
const cancelMapAnimation = (): void => {
  if (mapAnimationFrameId != null) {
    cancelAnimationFrame(mapAnimationFrameId);
    mapAnimationFrameId = null;
  }
};

/**
 * Smoothly animate map zoom only using step-based animation
 * Animates through zoom levels one step at a time with delays for visual smoothness
 */
const animateZoom = (mapRef: google.maps.Map, targetZoom: number, duration: number, onComplete?: () => void): void => {
  const startZoom = mapRef.getZoom();
  if (startZoom === undefined || Math.round(startZoom) === Math.round(targetZoom)) {
    onComplete?.();
    return;
  }

  const zoomSteps = Math.abs(Math.round(targetZoom) - Math.round(startZoom));
  if (zoomSteps === 0) {
    onComplete?.();
    return;
  }

  const stepDuration = duration / zoomSteps;
  const zoomDirection = targetZoom < startZoom ? -1 : 1;
  let currentStep = 0;

  const animateStep = (): void => {
    currentStep++;
    const newZoom = Math.round(startZoom) + currentStep * zoomDirection;
    mapRef.setZoom(newZoom);

    if (currentStep < zoomSteps) {
      setTimeout(animateStep, stepDuration);
    } else {
      onComplete?.();
    }
  };

  // Start the first step
  animateStep();
};

/**
 * Smoothly animate map pan only (zoom stays fixed during pan)
 */
const animatePan = (
  mapRef: google.maps.Map,
  targetCenter: google.maps.LatLng,
  duration: number,
  onComplete?: () => void
): void => {
  const startCenter = mapRef.getCenter();
  if (!startCenter) {
    mapRef.setCenter(targetCenter);
    onComplete?.();
    return;
  }

  const startLat = startCenter.lat();
  const startLng = startCenter.lng();
  const targetLat = targetCenter.lat();
  const targetLng = targetCenter.lng();

  if (startLat === targetLat && startLng === targetLng) {
    onComplete?.();
    return;
  }

  const startTime = performance.now();

  const animate = (currentTime: number): void => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeOutCubic(progress);

    const currentLat = startLat + (targetLat - startLat) * easedProgress;
    const currentLng = startLng + (targetLng - startLng) * easedProgress;
    mapRef.setCenter(new google.maps.LatLng(currentLat, currentLng));

    if (progress < 1) {
      mapAnimationFrameId = requestAnimationFrame(animate);
    } else {
      mapAnimationFrameId = null;
      onComplete?.();
    }
  };

  mapAnimationFrameId = requestAnimationFrame(animate);
};

/**
 * Check if a marker position is within the current map bounds
 */
export const isMarkerInBounds = (mapRef: google.maps.Map | null, latitude: number, longitude: number): boolean => {
  if (!mapRef) return false;

  const bounds = mapRef.getBounds();
  if (!bounds) return false;

  const markerLatLng = new google.maps.LatLng(latitude, longitude);
  return bounds.contains(markerLatLng);
};

/**
 * Calculate the zoom level needed to fit given bounds
 */
const getZoomForBounds = (mapRef: google.maps.Map, bounds: google.maps.LatLngBounds, padding: number = 50): number => {
  const mapDiv = mapRef.getDiv();
  const mapWidth = mapDiv.offsetWidth - padding * 2;
  const mapHeight = mapDiv.offsetHeight - padding * 2;

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  const latRange = ne.lat() - sw.lat();
  const lngRange = ne.lng() - sw.lng();

  const latZoom = Math.log2((mapHeight * 360) / (latRange * 256));
  const lngZoom = Math.log2((mapWidth * 360) / (lngRange * 256));

  return Math.floor(Math.min(latZoom, lngZoom, 20));
};

/**
 * Smoothly animate the map to fit all markers in the viewport when a marker
 * outside the current view is selected.
 *
 * This function will:
 * 1. Check if the target marker is already visible in the current bounds
 * 2. If not visible, smoothly animate zoom and pan to fit all markers
 *
 * @param mapRef - Reference to the Google Map instance
 * @param targetLatitude - Latitude of the selected marker
 * @param targetLongitude - Longitude of the selected marker
 * @param allMarkers - Array of all marker coordinates [{lat, lng}]
 */
export const animateMapToFitMarkers = (
  mapRef: google.maps.Map | null,
  targetLatitude: number,
  targetLongitude: number,
  allMarkers: Array<{ lat: number; lng: number }>
): void => {
  if (!mapRef || targetLatitude == null || targetLongitude == null) return;

  cancelMapAnimation();

  const targetLatLng = new google.maps.LatLng(targetLatitude, targetLongitude);

  // Check if marker is already visible
  if (isMarkerInBounds(mapRef, targetLatitude, targetLongitude)) {
    // Marker is visible, just pan smoothly to center it
    mapRef.panTo(targetLatLng);
    return;
  }

  // Marker is not visible - need to adjust view to include it
  // Create bounds that include all markers
  const bounds = new google.maps.LatLngBounds();

  // Add all markers to bounds
  allMarkers.forEach(marker => {
    if (marker.lat != null && marker.lng != null) {
      bounds.extend(new google.maps.LatLng(marker.lat, marker.lng));
    }
  });

  // Ensure the target marker is included
  bounds.extend(targetLatLng);

  const startZoom = mapRef.getZoom() ?? 10;
  const targetZoom = getZoomForBounds(mapRef, bounds, 60);
  const targetCenter = bounds.getCenter();

  // If we need to zoom out, do it in two steps: zoom out first, then pan
  // This keeps tiles loaded and looks smoother than simultaneous zoom+pan
  if (targetZoom < startZoom) {
    // Step 1: Zoom out over 400ms while keeping current center (tiles stay loaded)
    animateZoom(mapRef, targetZoom, ZOOM_ANIMATION_DURATION, () => {
      // Step 2: Pan to the target center over 500ms
      animatePan(mapRef, targetCenter, PAN_ANIMATION_DURATION);
    });
  } else {
    // No zoom out needed, just pan over 500ms
    animatePan(mapRef, targetCenter, PAN_ANIMATION_DURATION);
  }
};

/**
 * Function to handle scroll for mobile and tab view property cards
 */
export const handleScroll = (isFlexibleDateSearch: any, searchResults: any, activeIndex: any, mapRef: any) => {
  //adding activeIndex check to restrict recenter to propertycards only (not NEXT and BACK cards)
  if (searchResults && searchResults?.length && activeIndex < searchResults?.length) {
    handleMapRecenter(
      {
        lat: isFlexibleDateSearch
          ? searchResults[activeIndex]?.node?.basicInformation?.latitude
          : searchResults[activeIndex]?.node?.property?.basicInformation?.latitude,
        lng: isFlexibleDateSearch
          ? searchResults[activeIndex]?.node?.basicInformation?.longitude
          : searchResults[activeIndex]?.node?.property?.basicInformation?.longitude,
      },
      mapRef
    );
  }
};

const getMarkerCoordinates = (edge: any, isFlexibleDateSearch: boolean) => {
  if (isFlexibleDateSearch) {
    return {
      latitude: edge?.node?.basicInformation?.latitude,
      longitude: edge?.node?.basicInformation?.longitude,
    };
  }

  return {
    latitude: edge?.node?.property?.basicInformation?.latitude,
    longitude: edge?.node?.property?.basicInformation?.longitude,
  };
};

const getSelectedHotelCoordinates = (selectedHotel: any, isFlexibleDateSearch: boolean) => {
  if (isFlexibleDateSearch) {
    return {
      latitude: selectedHotel[0]?.node?.basicInformation?.latitude,
      longitude: selectedHotel[0]?.node?.basicInformation?.longitude,
    };
  }

  return {
    latitude: selectedHotel[0]?.node?.property?.basicInformation?.latitude,
    longitude: selectedHotel[0]?.node?.property?.basicInformation?.longitude,
  };
};

const isMarkerAtDifferentLocationThanSelectedHotel = (selectedHotel: any, edge: any, isFlexibleDateSearch: boolean) => {
  const selectedCoordinates = getSelectedHotelCoordinates(selectedHotel, isFlexibleDateSearch);
  const markerCoordinates = getMarkerCoordinates(edge, isFlexibleDateSearch);

  return (
    selectedCoordinates.latitude !== markerCoordinates.latitude &&
    selectedCoordinates.longitude !== markerCoordinates.longitude
  );
};

const getUnavailableMarkerLabelClass = (noRatesAreAvailable?: boolean) =>
  noRatesAreAvailable ? 'marker-label-black ' : 'marker-label-grey ';

const isDifferentHotelById = (
  selectedHotel: any,
  edge: any,
  isFlexibleDateSearch: boolean,
  noRatesAreAvailable?: boolean
) => {
  const shouldCompareByNodeId =
    (isFlexibleDateSearch || noRatesAreAvailable) && selectedHotel && selectedHotel?.length > 0;

  if (shouldCompareByNodeId) {
    return isFlexibleDateSearch
      ? selectedHotel[0]?.node?.id !== edge?.node?.id
      : selectedHotel[0]?.node?.property?.id !== edge?.node?.property?.id;
  }

  return selectedHotel[0]?.node?.property?.id !== edge?.node?.property?.id;
};

const isSameHotelByIdForSecondaryMarker = (selectedHotel: any, edge: any, isFlexibleDateSearch: boolean) => {
  const shouldCompareByNodeId = isFlexibleDateSearch && selectedHotel && selectedHotel?.length > 0;

  return shouldCompareByNodeId
    ? selectedHotel[0]?.node?.id === edge?.node?.id
    : selectedHotel[0]?.node?.property?.id === edge?.node?.property?.id;
};

const isDifferentHotelByIdForSecondaryMarker = (selectedHotel: any, edge: any, isFlexibleDateSearch: boolean) => {
  const shouldCompareByNodeId = isFlexibleDateSearch && selectedHotel && selectedHotel?.length > 0;

  return shouldCompareByNodeId
    ? selectedHotel[0]?.node?.id !== edge?.node?.id
    : selectedHotel[0]?.node?.property?.id !== edge?.node?.property?.id;
};

/**
 * Check if a marker matches the given hotel (selected or hovered)
 * Uses property ID comparison to ensure only the specific marker is matched,
 * not all markers at the same coordinate (important for stacked markers on mobile)
 */
const isMarkerMatchingHotel = (hotel: any, edge: any, isFlexibleDateSearch: boolean, isViewportL: boolean): boolean => {
  if (!hotel || !hotel.length) return false;

  if (isViewportL) {
    if (isFlexibleDateSearch) {
      return (
        hotel[0]?.node?.basicInformation?.latitude === edge?.node?.basicInformation?.latitude &&
        hotel[0]?.node?.basicInformation?.longitude === edge?.node?.basicInformation?.longitude
      );
    }

    return (
      hotel[0]?.node?.property?.basicInformation?.latitude === edge?.node?.property?.basicInformation?.latitude &&
      hotel[0]?.node?.property?.basicInformation?.longitude === edge?.node?.property?.basicInformation?.longitude
    );
  }

  if (isFlexibleDateSearch) {
    return hotel[0]?.node?.id === edge?.node?.id;
  }

  return hotel[0]?.node?.property?.id === edge?.node?.property?.id;
};

/**
 * Calculate the z-index for a marker based on its state.
 * Active markers (selected or hovered) get a higher z-index to appear on top.
 * For stacked markers on mobile, the base z-index is adjusted by duplicateCount.
 * Inactive markers get unique z-index based on their index to maintain stable stacking order.
 *
 * @param selectedHotel - Currently selected hotel(s)
 * @param hoveredHotel - Currently hovered hotel(s)
 * @param edge - The marker's edge data
 * @param isFlexibleDateSearch - Whether this is a flexible date search
 * @param isViewportL - Whether we're on desktop viewport
 * @param duplicateCount - The duplicate count for stacked markers (optional)
 * @param markerIndex - The index of the marker in the list (optional, for stable z-index ordering)
 * @returns z-index value for the marker
 */
export const getMarkerZIndex = (
  selectedHotel: any,
  hoveredHotel: any,
  edge: any,
  isFlexibleDateSearch: boolean,
  isViewportL: boolean,
  duplicateCount?: number,
  markerIndex?: number
): number => {
  // Mobile stacked markers: preserve click priority order.
  // Bottom marker (duplicateCount: 0) stays above stacked marker (duplicateCount: 1).
  if (!isViewportL) {
    return 1000 - (duplicateCount || 0);
  }

  const isSelectedMarker = isMarkerMatchingHotel(selectedHotel, edge, isFlexibleDateSearch, isViewportL);
  const isHoveredMarker = isMarkerMatchingHotel(hoveredHotel, edge, isFlexibleDateSearch, isViewportL);
  const isActiveMarker = isSelectedMarker || isHoveredMarker;

  // Desktop: active markers render above other markers
  if (isActiveMarker) {
    return 2000;
  }

  // Inactive markers get unique z-index based on their index to maintain stable stacking order.
  // This prevents z-index flickering when hovering fast between markers, as each marker
  // returns to its own unique z-index instead of all collapsing to the same value.
  // Using (index + 1) ensures z-index starts at 1, not 0.
  return (markerIndex ?? 0) + 1;
};

const getPrimaryMarkerBaseClass = (
  isViewportL: boolean,
  isUnavailableLabel: boolean,
  isFlexibleDateSearch: boolean,
  selectedHotel: any,
  edge: any,
  noRatesAreAvailable?: boolean,
  hoveredHotel?: any,
  isRateUnavailableLabel?: boolean
) => {
  const isSelectedMarker = isMarkerMatchingHotel(selectedHotel, edge, isFlexibleDateSearch, isViewportL);
  const isHoveredMarker = isMarkerMatchingHotel(hoveredHotel, edge, isFlexibleDateSearch, isViewportL);
  const isActiveMarker = isSelectedMarker || isHoveredMarker;

  // Both unavailable and rate unavailable markers should have the same styling behavior
  const isUnavailableType = isUnavailableLabel || isRateUnavailableLabel;

  let baseClass = 'm-map-pin ';

  if (isUnavailableType) {
    baseClass += getUnavailableMarkerLabelClass(noRatesAreAvailable);
  }
  if (!isActiveMarker) {
    return baseClass;
  }

  // For unavailable type markers, keep the same base class but add active state
  // This prevents size/padding changes on hover/active
  if (isUnavailableType) {
    return baseClass + 'active ';
  }

  let activeMarkerClass = 'm-map-pin t-label-alt-s active ';

  if (isFlexibleDateSearch || noRatesAreAvailable) {
    activeMarkerClass += ' icon-status-member-elite flex-map-active-pin ';
  }

  return activeMarkerClass;
};

/**
 * Function to compute logic for className of Primary marker and Secondary marker
 * This function is now completely being used for Baidu Maps
 */
export const getPrimaryMapMarkerClass = (
  typeOfMarker: any,
  isViewportL?: any,
  labelText?: any,
  isFlexibleDateSearch?: any,
  selectedHotel?: any,
  edge?: any,
  uniqueMarkersState?: any,
  index?: any,
  hideVar?: any,
  flexibleOrManyProperty?: any,
  firstMarkersList?: any,
  jsonContent?: any,
  generatedLabel?: any,
  noRatesAreAvailable?: boolean
) => {
  let result = '';
  const unavailableText = jsonContent?.unavailableLabel;
  if (typeOfMarker === 'primary') {
    if (isViewportL && labelText !== unavailableText) {
      result = 'm-map-pin ';
    } else if (isViewportL && labelText === unavailableText) {
      result = 'm-map-pin ';
      if (noRatesAreAvailable) {
        result += 'marker-label-black ';
      } else {
        result += 'marker-label-grey ';
      }
    } else if (
      !isViewportL &&
      (isFlexibleDateSearch
        ? selectedHotel[0]?.node?.basicInformation?.latitude !== edge?.node?.basicInformation?.latitude &&
          selectedHotel[0]?.node?.basicInformation?.longitude !== edge?.node?.basicInformation?.longitude
        : selectedHotel[0]?.node?.property?.basicInformation?.latitude !==
            edge?.node?.property?.basicInformation?.latitude &&
          selectedHotel[0]?.node?.property?.basicInformation?.longitude !==
            edge?.node?.property?.basicInformation?.longitude) &&
      labelText !== unavailableText
    ) {
      result = 'm-map-pin ';
    } else if (
      !isViewportL &&
      (isFlexibleDateSearch
        ? selectedHotel[0]?.node?.basicInformation?.latitude !== edge?.node?.basicInformation?.latitude &&
          selectedHotel[0]?.node?.basicInformation?.longitude !== edge?.node?.basicInformation?.longitude
        : selectedHotel[0]?.node?.property?.basicInformation?.latitude !==
            edge?.node?.property?.basicInformation?.latitude &&
          selectedHotel[0]?.node?.property?.basicInformation?.longitude !==
            edge?.node?.property?.basicInformation?.longitude) &&
      labelText === unavailableText
    ) {
      result = 'm-map-pin ';
      if (noRatesAreAvailable) {
        result += 'marker-label-black ';
      } else {
        result += 'marker-label-grey ';
      }
    } else {
      result = 'm-map-pin t-label-alt-s active ';
      if (isFlexibleDateSearch || noRatesAreAvailable) {
        result += ' icon-status-member-elite flex-map-active-pin ';
      }
    }

    if (isFlexibleDateSearch || noRatesAreAvailable) {
      result += 'icon-status-member-elite flex-map-active-pin ';
    }

    if (hideVar) {
      result += 'hide-only ';
    }
    if (firstMarkersList && !uniqueMarkersState[index].duplicate) {
      result += 'first-marker ';
    }
    if (
      !isFlexibleDateSearch &&
      !noRatesAreAvailable &&
      (labelText === jsonContent?.openingSoonLabel || labelText === jsonContent?.rateUnavailableLabel) &&
      ((isFlexibleDateSearch || noRatesAreAvailable) && selectedHotel && selectedHotel?.length > 0
        ? selectedHotel[0]?.node?.id !== edge?.node?.id
        : selectedHotel[0]?.node?.property?.id !== edge?.node?.property?.id)
    ) {
      result += 'marker-label-grey ';
    }
    if (
      (isFlexibleDateSearch || noRatesAreAvailable) &&
      (labelText === jsonContent?.openingSoonLabel || labelText === jsonContent?.rateUnavailableLabel) &&
      ((isFlexibleDateSearch || noRatesAreAvailable) && selectedHotel && selectedHotel?.length > 0
        ? selectedHotel[0]?.node?.id !== edge?.node?.id
        : selectedHotel[0]?.node?.property?.id !== edge?.node?.property?.id)
    ) {
      result += 'marker-label-black ';
    }

    if (flexibleOrManyProperty || noRatesAreAvailable) {
      result += 'icon-status-member-elite flex-map-pin ';
    }
    if (
      isFlexibleDateSearch &&
      selectedHotel &&
      selectedHotel?.length > 0 &&
      new Date(edge?.node?.basicInformation?.openingDate) > new Date() &&
      selectedHotel[0]?.node?.id !== edge?.node?.id
    ) {
      result += 'marker-label-grey ';
    }
    result += `pin-${index} `;
  } else if (typeOfMarker === 'secondary') {
    result += `add-on-${index} ${generatedLabel?.className}`;
    if (hideVar) {
      result += 'hide-only ';
    }
    if (
      labelText === jsonContent.openingSoonLabel &&
      (isFlexibleDateSearch && selectedHotel && selectedHotel?.length > 0
        ? selectedHotel[0]?.node?.id !== edge?.node?.id
        : selectedHotel[0]?.node?.property?.id !== edge?.node?.property?.id)
    ) {
      result += 'opening-new-hotel';
    }
    if (
      labelText === jsonContent.openingSoonLabel &&
      (isFlexibleDateSearch && selectedHotel && selectedHotel?.length > 0
        ? selectedHotel[0]?.node?.id === edge?.node?.id
        : selectedHotel[0]?.node?.property?.id === edge?.node?.property?.id)
    ) {
      result += 'active-new-hotel';
    }

    // NEW: reduce top offset when an add-on label exists AND primary text is "Rate Unavailable"
    if (generatedLabel?.label && labelText === jsonContent?.rateUnavailableLabel) {
      result += ' addon-with-unavailable';
    }
  }
  return result;
};

/**
 * Function to compute logic for new classNames of Primary marker and Secondary marker
 * This function is now completely being used for Google Maps
 */
export const getUpdatedPrimaryMapMarkerClass = (
  typeOfMarker: any,
  isViewportL?: any,
  labelText?: any,
  isFlexibleDateSearch?: any,
  selectedHotel?: any,
  edge?: any,
  uniqueMarkersState?: any,
  index?: any,
  hideVar?: any,
  flexibleOrManyProperty?: any,
  firstMarkersList?: any,
  jsonContent?: any,
  generatedLabel?: any,
  noRatesAreAvailable?: boolean,
  hoveredHotel?: any
) => {
  let result = '';
  const unavailableText = jsonContent?.unavailableLabel;
  const isUnavailableLabel = labelText === unavailableText;
  const isOpeningSoonLabel = labelText === jsonContent?.openingSoonLabel;
  const isRateUnavailableLabel = labelText === jsonContent?.rateUnavailableLabel;
  const isFlexibleOrNoRates = isFlexibleDateSearch || noRatesAreAvailable;

  if (typeOfMarker === 'primary') {
    result = getPrimaryMarkerBaseClass(
      isViewportL,
      isUnavailableLabel,
      isFlexibleDateSearch,
      selectedHotel,
      edge,
      noRatesAreAvailable,
      hoveredHotel,
      isRateUnavailableLabel
    );

    if (isFlexibleOrNoRates) {
      result += 'icon-status-member-elite flex-map-active-pin ';
    }

    if (hideVar) {
      result += 'hide-only ';
    }

    if (firstMarkersList && !uniqueMarkersState[index].duplicate) {
      result += 'first-marker ';
    }

    const isOpeningSoonOrRateUnavailable = isOpeningSoonLabel || isRateUnavailableLabel;
    const isDifferentHotel = isDifferentHotelById(selectedHotel, edge, isFlexibleDateSearch, noRatesAreAvailable);
    const isHoveredMarker = isMarkerMatchingHotel(hoveredHotel, edge, isFlexibleDateSearch, isViewportL);

    // Only add grey/black class if marker is not being hovered
    if (
      !isFlexibleDateSearch &&
      !noRatesAreAvailable &&
      isOpeningSoonOrRateUnavailable &&
      isDifferentHotel &&
      !isHoveredMarker
    ) {
      result += 'marker-label-grey ';
    }

    if (isFlexibleOrNoRates && isOpeningSoonOrRateUnavailable && isDifferentHotel && !isHoveredMarker) {
      result += 'marker-label-black ';
    }

    if (flexibleOrManyProperty || noRatesAreAvailable) {
      result += 'icon-status-member-elite flex-map-pin ';
    }

    const isFutureOpeningHotel =
      isFlexibleDateSearch &&
      selectedHotel &&
      selectedHotel?.length > 0 &&
      new Date(edge?.node?.basicInformation?.openingDate) > new Date() &&
      selectedHotel[0]?.node?.id !== edge?.node?.id;

    if (isFutureOpeningHotel) {
      result += 'marker-label-grey ';
    }

    result += `pin-${index} `;
  } else if (typeOfMarker === 'secondary') {
    result += `add-on-${index} ${generatedLabel?.className}`;

    if (hideVar) {
      result += 'hide-only ';
    }

    if (labelText === jsonContent.openingSoonLabel) {
      if (isDifferentHotelByIdForSecondaryMarker(selectedHotel, edge, isFlexibleDateSearch)) {
        result += 'opening-new-hotel';
      }

      if (isSameHotelByIdForSecondaryMarker(selectedHotel, edge, isFlexibleDateSearch)) {
        result += 'active-new-hotel';
      }
    }

    // Reduce top offset when an add-on label exists AND primary text is "Rate Unavailable"
    if (generatedLabel?.label && isRateUnavailableLabel) {
      result += ' addon-with-unavailable';
    }
  }

  return result;
};

/**
 * Function to compute logic for color of Primary marker
 */
export const getPrimaryMarkerColor = (
  isFlexibleDateSearch: any,
  selectedHotel: any,
  edge: any,
  noRatesAreAvailable: boolean,
  hoveredHotel: any,
  isViewportL: boolean
) => {
  const isSelectedMarker = isMarkerMatchingHotel(selectedHotel, edge, isFlexibleDateSearch, isViewportL);
  const isHoveredMarker = isMarkerMatchingHotel(hoveredHotel, edge, isFlexibleDateSearch, isViewportL);
  const isActiveMarker = isSelectedMarker || isHoveredMarker;

  if ((isFlexibleDateSearch || noRatesAreAvailable) && isActiveMarker) {
    return 'transparent';
  } else if (!isActiveMarker) {
    return 'white';
  } else {
    return 'black';
  }
};

/**
 * heights helps to memoize the prev stack cards height
 */
const heights: any = {
  0: 0,
};

/**
 * To memoize the cardFeePresent for the previous stack cards
 */
const cardFeePresentMemo: boolean[] = [];
const cardBadgePresentMemo: string[] = [];

/**
 * Function to calculate the heights of prev stack cards height
 */
export const calculateCardHeight = (selectedHotel: any, index: number, hasLabel: any, cardFeePresent: boolean) => {
  // Base case: if index is 0, return 0
  if (index === 0 && selectedHotel?.length !== 0) {
    cardFeePresentMemo[index] = cardFeePresent; // Memoize the first card's cardFeePresent
    cardBadgePresentMemo[index] = hasLabel;
    return 0;
  }
  // Calculate the card height based on conditions
  let cardHeight = 0;
  const prevCardFeePresent = cardFeePresentMemo[index - 1];
  const prevCardBadgePresent = cardBadgePresentMemo[index - 1]?.length > 0;
  if (prevCardBadgePresent) {
    cardHeight = prevCardFeePresent ? LABEL_FEE_CARD_POSITION : LABEL_CARD_POSITION;
  } else {
    cardHeight = prevCardFeePresent ? FEES_CARD_POSITION : DEFAULT_CARD_POSITION;
  }
  // Accumulate the current card's height with the calculated height
  heights[index] = cardHeight;
  cardFeePresentMemo[index] = cardFeePresent; // Memoize the current card's cardFeePresent
  cardBadgePresentMemo[index] = hasLabel;
  // Recursively calculate the card height for the card above and pass the updated card height
  return heights[index - 1] + cardHeight;
};

/**
 * Returns the rendered height of a single info window hotel card based on
 * badge and mandatory fee / taxes-and-fees state.
 */
export const getInfoWindowCardHeight = (hasLabel: boolean, cardFeePresent: boolean): number => {
  if (hasLabel) {
    return cardFeePresent ? Math.abs(LABEL_FEE_CARD_POSITION) : Math.abs(LABEL_CARD_POSITION);
  }

  return cardFeePresent ? Math.abs(LABEL_CARD_POSITION) : Math.abs(DEFAULT_CARD_POSITION);
};

/**
 * markerHeight helps to memoize the prev marker height in case of stack pins
 */
const markerHeight: any = { 0: 0 };
/**
 * Additional offset per duplicate marker for better visual separation
 */
const DUPLICATE_MARKER_OFFSET = 1;

export const getMarkerHeight = (index: number, isFlexibleDateSearch: boolean) => {
  // Base case: If index is 0 or there's no selectedHotel data, return 20.
  if (index === 0) {
    return 20;
  }
  const PinHeight = isFlexibleDateSearch ? PIN_FLEXI_HEIGHT : PIN_HEIGHT;
  // Recursively get the height of the marker for the previous index.
  const previousMarkerHeight = getMarkerHeight(index - 1, isFlexibleDateSearch);
  // Set the current marker height in the markerHeight object and add the previous height.
  // Add DUPLICATE_MARKER_OFFSET (2px) for each duplicate marker for better visual separation
  markerHeight[index] = PinHeight + DUPLICATE_MARKER_OFFSET + previousMarkerHeight;
  return markerHeight[index];
};

/**
 * Function to find first marker in case of double stack markers
 */
export const findFirstMarker = (marker: any, firstMarkers: any): boolean => {
  let result = false;
  firstMarkers.forEach((fm: any) => {
    if (
      fm.latitude === marker.latitude &&
      fm.longitude === marker.longitude &&
      fm.duplicate !== marker.duplicate &&
      fm.propertyId !== marker.propertyId
    ) {
      result = true;
    }
  });
  return result;
};
/**
 * Function to find the index of the first duplicate marker in case of stack pins
 */
export const findFirstDuplicatePosition = (hotel: any) => {
  const markerEncountered: any = {}; // Use an object to store encountered elements and their positions
  for (let i = 0; i < hotel?.length; i++) {
    const currentElement: any = hotel[i]?.node?.property?.basicInformation?.latitude;
    // Check if the current element has been encountered before
    if (markerEncountered[currentElement] !== undefined) {
      return markerEncountered[currentElement]; // Return the position of the first duplicate
    } else {
      markerEncountered[currentElement] = i; // Store the position of the current element
    }
  }
  return -1; // Return -1 if no duplicates are found
};

export const getUniqueMarkers = (searchResults: any, isFlexibleDateSearch: any, uniqueMarkers: any) => {
  if (searchResults?.length) {
    const coords = new Set();
    const duplicates = new Map();
    // First pass to identify duplicates and store their minimum index
    for (const [i, obj] of searchResults.entries()) {
      const markerData = isFlexibleDateSearch ? obj?.node : obj?.node.property;
      const lat = markerData?.basicInformation?.latitude;
      const long = markerData?.basicInformation?.longitude;
      const coord = `${lat},${long}`;

      if (coords.has(coord)) {
        // If duplicate, store the minimum index
        const existingIndex = duplicates.get(coord);
        if (i < existingIndex) {
          duplicates.set(coord, i);
        }
      } else {
        coords.add(coord);
        duplicates.set(coord, i);
      }
    }

    // Second pass to build uniqueMarkers
    for (const [i, obj] of searchResults.entries()) {
      const markerData = isFlexibleDateSearch ? obj?.node : obj?.node.property;
      const lat = markerData?.basicInformation?.latitude;
      const long = markerData?.basicInformation?.longitude;
      const coord = `${lat},${long}`;

      const isDuplicate = duplicates.get(coord) !== i;

      uniqueMarkers.push({
        latitude: markerData?.basicInformation?.latitude,
        longitude: markerData?.basicInformation?.longitude,
        duplicate: isDuplicate,
        propertyId: markerData?.id,
        duplicateCount: 0,
      });
    }

    const duplicateCoordsParsed: any = []; // array to store the coordinates whenever we have parsed the coordinates more than once

    //below part of code is to parse through uniqueMarkers and find and assign a count to those coordinates which are duplicate.
    //so in case of single pin, duplicateCount will be 0 , for double stack pins , count will be 0 and 1 , for triple stack 0,1,2 and so on
    for (let n = 0; n < uniqueMarkers?.length; n++) {
      if (uniqueMarkers?.[n].duplicate) {
        const lat = uniqueMarkers?.[n]?.latitude;
        const long = uniqueMarkers?.[n]?.longitude;
        const coord = `${lat},${long}`;
        if (duplicateCoordsParsed?.includes(coord)) {
          uniqueMarkers[n].duplicateCount = uniqueMarkers?.[n]?.duplicateCount + 2;
        } else {
          uniqueMarkers[n].duplicateCount = uniqueMarkers?.[n]?.duplicateCount + 1;
          duplicateCoordsParsed.push(coord);
        }
      }
    }
    return uniqueMarkers;
  }
};

/**
 * Function to find and store duplicate coordinates
 */
export function findAndStoreDuplicates(data: any) {
  const duplicateCoordinates = new Set();
  const uniqueCoordinates = new Set();
  data?.forEach((item: any) => {
    const { node } = item;
    const { property } = node;
    const latitude = property?.basicInformation?.latitude ?? node?.basicInformation?.latitude;
    const coordinate = latitude;
    if (uniqueCoordinates.has(coordinate)) {
      duplicateCoordinates.add(coordinate);
    } else {
      uniqueCoordinates.add(coordinate);
    }
  });
  return Array.from(duplicateCoordinates);
}

/**
 * Function to set the centre of map when map is loaded and lat Lng values are not available
 */
export const handleMapCentre = (searchResults: any, query: any, setCentreValue: any, isFlexibleDateSearch: any) => {
  if (
    searchResults?.length &&
    (Number(query?.longitude) === 0 ||
      query?.longitude === null ||
      query?.longitude?.toString() === '' ||
      query?.latitude === undefined)
  ) {
    setCentreValue({
      lat: isFlexibleDateSearch
        ? Number(searchResults[0]?.node?.basicInformation?.latitude)
        : Number(searchResults[0]?.node?.property?.basicInformation?.latitude),
      lng: isFlexibleDateSearch
        ? Number(searchResults[0]?.node?.basicInformation?.longitude)
        : Number(searchResults[0]?.node?.property?.basicInformation?.longitude),
    });
  }
};

/**
 * Function to capture the marker click & save it in the context
 * @param {number}   index specifices the index of the card incase of stacked cards
 */
export const handleSelectedHotel = (
  index: number,
  searchResults: any,
  isViewportL: any,
  setActiveIndex: any,
  setSelectedHotel: any,
  isFlexibleDateSearch: any
) => {
  if (index < 0) {
    return;
  }
  const edge: any = searchResults && searchResults[index] ? searchResults[index] : null;
  if (!isViewportL) {
    setActiveIndex(index);
    setSelectedHotel(() => [edge]);
  } else {
    const finalData = isFlexibleDateSearch
      ? searchResults?.filter(
          (hotelList: any) =>
            hotelList?.node?.basicInformation?.latitude === edge?.node.basicInformation?.latitude &&
            hotelList?.node?.basicInformation?.longitude === edge?.node.basicInformation?.longitude &&
            hotelList?.node?.id !== edge?.node.id
        )
      : searchResults?.filter(
          (hotelList: any) =>
            hotelList.node.property.basicInformation?.latitude === edge?.node.property.basicInformation?.latitude &&
            hotelList.node.property.basicInformation?.longitude === edge?.node.property.basicInformation?.longitude &&
            hotelList.node.property.id !== edge?.node.property.id
        );

    if (finalData?.length) {
      setSelectedHotel([...finalData, edge]);
    } else {
      setSelectedHotel(() => [edge]);
    }
  }
};

//function to assign set boundaries - THIS WILL OMIT MAP CUSTOM ZOOM VALUES SETTING , hence removed setZoomValue from above function
export const fitBounds = (mapRef: any, bounds: any) => {
  if (mapRef && bounds) {
    mapRef.fitBounds(bounds);
  }
};

//function to set boundaries for maps based on the markers latitude and longitude
export const callFitBounds = (uniqueMarkersState: any, isLoaded: any, setBounds: any) => {
  if (uniqueMarkersState?.length > 0 && isLoaded) {
    const bounds = new window.google.maps.LatLngBounds();
    uniqueMarkersState?.forEach((marker: any) => {
      if (marker.latitude && marker.longitude) {
        bounds.extend({ lat: marker.latitude, lng: marker.longitude });
      }
    });
    setBounds(bounds);
  }
};

/**
 * Function to handle scroll for mobile and tab view property cards
 */
export const handleBMapScroll = (
  isFlexibleDateSearch: boolean,
  searchResults: Array<{
    node: {
      basicInformation: { latitude: number; longitude: number };
      property: { basicInformation: { latitude: number; longitude: number } };
    };
  }>,
  activeIndex: number,
  mapRef: MapRefs
) => {
  //adding activeIndex check to restrict recenter to propertycards only (not NEXT and BACK cards)
  if (searchResults && searchResults?.length && activeIndex < searchResults?.length) {
    handleBMapRecenter(
      isFlexibleDateSearch
        ? searchResults[activeIndex]?.node?.basicInformation?.latitude
        : searchResults[activeIndex]?.node?.property?.basicInformation?.latitude,
      isFlexibleDateSearch
        ? searchResults[activeIndex]?.node?.basicInformation?.longitude
        : searchResults[activeIndex]?.node?.property?.basicInformation?.longitude,
      mapRef
    );
  }
};

export const handleMarkerClick = (
  index: number,
  searchResults: any,
  isViewportL: any,
  setActiveIndex: any,
  setSelectedHotel: any,
  isFlexibleDateSearch: any,
  setOpen: any
) => {
  setTimeout(() => {
    handleSelectedHotel(index, searchResults, isViewportL, setActiveIndex, setSelectedHotel, isFlexibleDateSearch);
    setOpen(true);
  }, 180);
  setTimeout(() => {
    // This QuerySelector can't be removed, as this class is from Google maps library.
    document.getElementsByClassName('gm-style-iw-tc')[1]?.classList.add('d-none');
  }, 200);
};
